import './Overlay.css';
import { useState, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';

function Overlay() {
  const [currentTime, setCurrentTime] = useState('');
  const [balance, setBalance] = useState(0);
  const [displayBalance, setDisplayBalance] = useState(0);
  const [animationClass, setAnimationClass] = useState('');
  const [currentDay, setCurrentDay] = useState(1);
  const [buyNotifications, setBuyNotifications] = useState([]);
  const [subscriptionNotifications, setSubscriptionNotifications] = useState([]);
  const [currentChallenge, setCurrentChallenge] = useState(null);
  const [imageDisplay, setImageDisplay] = useState({ imageData: null, text: null, visible: false });
  const kickWsRef = useRef(null);
  const isReconnectingKickRef = useRef(false);
  const reconnectKickTimeoutRef = useRef(null);

  // Set body class for transparency
  useEffect(() => {
    document.body.classList.add('overlay-active');
    document.body.style.backgroundColor = 'transparent';
    document.documentElement.style.backgroundColor = 'transparent';
    
    return () => {
      document.body.classList.remove('overlay-active');
      document.body.style.backgroundColor = '';
      document.documentElement.style.backgroundColor = '';
    };
  }, []);

  // Update clock every second
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      // Bali, Indonesia time (WITA)
      const options = {
        timeZone: 'Asia/Makassar',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      };
      const timeString = now.toLocaleTimeString('en-AU', options);
      setCurrentTime(timeString);
    };

    updateTime();
    const interval = setInterval(updateTime, 1000);

    return () => clearInterval(interval);
  }, []);

  // Calculate current day (Day 1 = November 13, 2025)
  useEffect(() => {
    const calculateDay = () => {
      // Get current date in Bali timezone
      const now = new Date();
      const baliDateString = now.toLocaleDateString('en-CA', { timeZone: 'Asia/Makassar' }); // YYYY-MM-DD format
      
      // Parse the date
      const [year, month, day] = baliDateString.split('-').map(Number);
      const baliDate = new Date(year, month - 1, day);
      
      // Parse start date in Bali timezone (November 13, 2025)
      const startYear = 2025;
      const startMonth = 10; // November (0-indexed)
      const startDay = 13;
      const startDateBali = new Date(startYear, startMonth, startDay);
      
      // Calculate difference in days
      const diffTime = baliDate - startDateBali;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24)) + 1;
      
      // Ensure day is at least 1 and at most 365
      const dayNumber = Math.max(1, Math.min(365, diffDays));
      setCurrentDay(dayNumber);
    };

    // Calculate day immediately
    calculateDay();
    
    // Update every minute to catch day changes at midnight (Bali timezone)
    // This ensures the day updates automatically without requiring a page refresh
    const intervalId = setInterval(calculateDay, 60 * 1000);

    return () => clearInterval(intervalId);
  }, []);

  // Connect to backend for balance updates
  useEffect(() => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://apidbrapi.arubio.dev';
    const socket = io(backendUrl, {
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000, // Faster reconnection to minimize message loss
      reconnectionDelayMax: 5000, // Max delay for exponential backoff
      reconnectionAttempts: Infinity, // Unlimited reconnection attempts
      timeout: 20000 // Connection timeout
    });

    socket.on('connect', () => {
      console.log('Connected to backend for overlay');
      // Request current balance on connect
      socket.emit('getBalance');
      
      // Fetch current challenge on connect
      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://apidbrapi.arubio.dev';
      fetch(`${backendUrl}/api/challenges/current`)
        .then(res => res.json())
        .then(data => {
          if (data.currentChallenge) {
            setCurrentChallenge(data.currentChallenge);
          }
        })
        .catch(err => console.error('Error fetching current challenge:', err));
      
      // Fetch current image display on connect
      fetch(`${backendUrl}/api/image-display`)
        .then(res => res.json())
        .then(data => {
          setImageDisplay(data);
        })
        .catch(err => console.error('Error fetching image display:', err));
    });

    socket.on('balanceUpdate', (newBalance) => {
      console.log('Balance update received:', newBalance);
      
      // Store current balance before updating
      setBalance((currentBalance) => {
        // Trigger animation based on change
        if (newBalance > currentBalance) {
          console.log('Balance increased!');
          setAnimationClass('balance-increase');
        } else if (newBalance < currentBalance) {
          console.log('Balance decreased!');
          setAnimationClass('balance-decrease');
        } else {
          console.log('Balance set to same value');
          setAnimationClass('balance-set');
        }
        
        // Animate the number counting
        animateCounter(currentBalance, newBalance);
        
        // Remove animation class after animation completes (match counter duration)
        setTimeout(() => setAnimationClass(''), 1500);
        
        return newBalance;
      });
    });

    const animateCounter = (start, end) => {
      const duration = 1500; // milliseconds
      const startTime = Date.now();
      const difference = end - start;
      
      const updateCounter = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        
        // Ease out cubic for smooth deceleration
        const easeProgress = 1 - Math.pow(1 - progress, 3);
        
        const current = start + (difference * easeProgress);
        setDisplayBalance(current);
        
        if (progress < 1) {
          requestAnimationFrame(updateCounter);
        } else {
          setDisplayBalance(end);
        }
      };
      
      requestAnimationFrame(updateCounter);
    };

    socket.on('disconnect', (reason) => {
      console.log('Disconnected from backend:', reason);
    });

    // Listen for buy transactions
    socket.on('buyTransaction', (data) => {
      console.log('Buy transaction received:', data);
      
      const notificationId = Date.now();
      const notification = {
        id: notificationId,
        usdAmount: data.usdAmount,
        solAmount: data.solAmount
      };
      
      setBuyNotifications(prev => [...prev, notification]);
      
      // Remove notification after 10 seconds
      setTimeout(() => {
        setBuyNotifications(prev => prev.filter(n => n.id !== notificationId));
      }, 10000);
    });

    // Listen for challenge updates
    socket.on('challengesUpdate', (challengeData) => {
      console.log('Challenge update received:', challengeData);
      setCurrentChallenge(challengeData.currentChallenge || null);
    });

    // Listen for image display updates
    socket.on('imageDisplayUpdate', (data) => {
      console.log('Image display update received:', data);
      setImageDisplay(data);
    });

    // Cleanup on unmount
    return () => {
      socket.disconnect();
    };
  }, []);

  // Connect to Kick WebSocket for subscription events
  useEffect(() => {
    const connectKick = () => {
      // If already connected, don't reconnect
      if (kickWsRef.current && kickWsRef.current.readyState === WebSocket.OPEN) {
        return;
      }
      
      // If already reconnecting, don't start another reconnection
      if (isReconnectingKickRef.current) {
        return;
      }
      
      // Clean up old connection if it exists but is closed
      if (kickWsRef.current) {
        const oldWs = kickWsRef.current;
        oldWs.onopen = null;
        oldWs.onmessage = null;
        oldWs.onerror = null;
        oldWs.onclose = null;
        if (oldWs.readyState !== WebSocket.CLOSED && oldWs.readyState !== WebSocket.CLOSING) {
          oldWs.close();
        }
        kickWsRef.current = null;
      }
      
      // Clear any pending reconnection timeout
      if (reconnectKickTimeoutRef.current) {
        clearTimeout(reconnectKickTimeoutRef.current);
        reconnectKickTimeoutRef.current = null;
      }
      
      const kickWs = new WebSocket('wss://ws-us2.pusher.com/app/32cbd69e4b950bf97679?protocol=7&client=js&version=8.4.0&flash=false');
      kickWsRef.current = kickWs;

      kickWs.onopen = () => {
        console.log('Kick WebSocket connected (overlay)');
        isReconnectingKickRef.current = false;
        
        // Subscribe to channel subscription events
        kickWs.send(JSON.stringify({
          event: "pusher:subscribe",
          data: { auth: "", channel: "channel.23886990" }
        }));
      };

      kickWs.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Handle channel subscription events
          if (data.event === "App\\Events\\ChannelSubscriptionEvent") {
            try {
              const subData = JSON.parse(data.data);
              // Only show single user subscriptions (not multi-user gift subs)
              if (subData.username && subData.username !== "" && subData.user_ids && subData.user_ids.length === 1) {
                console.log(`[OVERLAY SUB] Username: ${subData.username}`);
                
                const notificationId = Date.now();
                const notification = {
                  id: notificationId,
                  username: subData.username,
                  isGift: false,
                  giftCount: 0
                };
                
                setSubscriptionNotifications(prev => [...prev, notification]);
                
                // Remove notification after 8 seconds
                setTimeout(() => {
                  setSubscriptionNotifications(prev => prev.filter(n => n.id !== notificationId));
                }, 8000);
              }
            } catch (error) {
              console.error('Error parsing subscription event:', error);
            }
          }
          
          // Handle gifted subscription events
          if (data.event === "App\\Events\\LuckyUsersWhoGotGiftSubscriptionsEvent") {
            try {
              const giftData = JSON.parse(data.data);
              if (giftData.gifter_username && giftData.usernames && giftData.usernames.length > 0) {
                console.log(`[OVERLAY GIFT] ${giftData.gifter_username} has gifted ${giftData.usernames.length} subs`);
                
                const notificationId = Date.now();
                const notification = {
                  id: notificationId,
                  username: giftData.gifter_username,
                  isGift: true,
                  giftCount: giftData.usernames.length
                };
                
                setSubscriptionNotifications(prev => [...prev, notification]);
                
                // Remove notification after 8 seconds
                setTimeout(() => {
                  setSubscriptionNotifications(prev => prev.filter(n => n.id !== notificationId));
                }, 8000);
              }
            } catch (error) {
              console.error('Error parsing gift subscription event:', error);
            }
          }
        } catch (error) {
          console.error('Error parsing Kick message:', error);
        }
      };

      kickWs.onerror = (error) => {
        console.error('Kick WebSocket error (overlay):', error);
      };

      kickWs.onclose = (event) => {
        console.log('Kick WebSocket closed (overlay)', event.code, event.reason);
        
        // Don't reconnect if already reconnecting
        if (isReconnectingKickRef.current) {
          return;
        }
        
        // Don't reconnect if this was a manual close (code 1000 = normal closure)
        if (event.code === 1000) {
          console.log('WebSocket closed normally, not reconnecting');
          return;
        }
        
        // Mark as reconnecting to prevent multiple reconnection attempts
        isReconnectingKickRef.current = true;
        
        // Reconnect quickly (1 second)
        reconnectKickTimeoutRef.current = setTimeout(() => {
          isReconnectingKickRef.current = false;
          connectKick();
        }, 1000);
      };
    };

    connectKick();

    // Cleanup on unmount
    return () => {
      // Clear reconnection timeout
      if (reconnectKickTimeoutRef.current) {
        clearTimeout(reconnectKickTimeoutRef.current);
        reconnectKickTimeoutRef.current = null;
      }
      
      // Mark as not reconnecting to prevent reconnection after unmount
      isReconnectingKickRef.current = false;
      
      if (kickWsRef.current) {
        // Remove event handlers
        kickWsRef.current.onopen = null;
        kickWsRef.current.onmessage = null;
        kickWsRef.current.onerror = null;
        kickWsRef.current.onclose = null;
        // Close connection
        if (kickWsRef.current.readyState !== WebSocket.CLOSED && kickWsRef.current.readyState !== WebSocket.CLOSING) {
          kickWsRef.current.close();
        }
        kickWsRef.current = null;
      }
    };
  }, []);

  return (
    <div className="overlay">
      {/* Current Challenge Display at Top */}
      {currentChallenge && (
        <div className="overlay-challenge-banner">
          <span className="overlay-challenge-label">CHALLENGE:</span>
          <span className="overlay-challenge-text">{currentChallenge.text}</span>
        </div>
      )}
      
      <div className="overlay-top-left">
        <div className="info-row">
          <svg className="calendar-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
          </svg>
          <span className="day-text">Day {currentDay}/365</span>
        </div>
        <div className="info-row">
          <svg className="clock-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm.5-13H11v6l5.2 3.2.8-1.3-4.5-2.7V7z"/>
          </svg>
          <span className="time-display">{currentTime}</span>
        </div>
        <div className="info-row">
          <svg className="location-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
          </svg>
          <span className="location-display">Bali, Indonesia</span>
        </div>
      </div>
      
      <div className={`overlay-top-right ${animationClass}`}>
        <span className="balance-label">Balance:</span>
        <span className="balance-amount">${displayBalance.toFixed(2)}</span>
      </div>

      {/* Buy Notifications */}
      <div className="buy-notifications">
        {buyNotifications.map((notification) => (
          <div key={notification.id} className="buy-notification">
            <div className="buy-notification-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
            </div>
            <div className="buy-notification-content">
              <span className="buy-notification-title">BUY</span>
              <span className="buy-notification-amount">${notification.usdAmount.toFixed(2)}</span>
              <span className="buy-notification-sol">{notification.solAmount.toFixed(2)} SOL</span>
            </div>
          </div>
        ))}
      </div>

      {/* Subscription Notifications - Top Center */}
      <div className="subscription-notifications-top">
        {subscriptionNotifications.map((notification) => (
          <div key={notification.id} className={`subscription-notification-top ${notification.isGift ? 'gift-subscription' : ''}`}>
            <div className="subscription-content-wrapper">
              <div className="subscription-icon-wrapper">
                <svg className="subscription-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 6h-2.18c.11-.31.18-.65.18-1a2.996 2.996 0 0 0-5.5-1.65l-.5.67-.5-.68C10.96 2.54 10 2 9 2 7.34 2 6 3.34 6 5c0 .35.07.69.18 1H4c-1.11 0-1.99.89-1.99 2L2 19c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V8c0-1.11-.89-2-2-2zm-5-2c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM9 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm11 15H4v-2h16v2zm0-5H4V8h5.08L7 10.83 8.62 12 11 8.76l1-1.36 1 1.36L15.38 12 17 10.83 14.92 8H20v6z"/>
                </svg>
              </div>
              <div className="subscription-text-content">
                <div className="subscription-label">{notification.isGift ? 'GIFTED SUBSCRIPTION' : 'NEW SUBSCRIBER'}</div>
                <div className="subscription-username">{notification.username}</div>
                {notification.isGift && (
                  <div className="subscription-gift-info">Gifted {notification.giftCount} sub{notification.giftCount !== 1 ? 's' : ''}!</div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Image Display (Bottom Right) */}
      {imageDisplay.visible && imageDisplay.imageData && (
        <div className="overlay-image-display">
          <div className="overlay-image-wrapper">
            {imageDisplay.text && (
              <div className="overlay-image-display-text">{imageDisplay.text}</div>
            )}
            <img src={imageDisplay.imageData} alt="Display" className="overlay-image-display-img" />
          </div>
        </div>
      )}
    </div>
  );
}

export default Overlay;

